/**
 * What the schedule row says, and when it says something else.
 *
 * One rule, in one place, because the row and the booking footer used to
 * derive their copy independently and could therefore disagree about the same
 * window. The row picked a fact by priority order — gates, then opens, then
 * closes — which is how a visitor ended up reading "Bookings opened at 4:04 AM"
 * about a window that opened three weeks ago, while never being told when
 * booking actually ends.
 *
 * This module picks by clock position instead: the first moment still ahead of
 * `now` is the one worth a sentence. Past facts appear only when nothing is
 * ahead of them.
 *
 * Pure, and parameterised on `now` and `timeZone`, so the interesting cases
 * are testable without a live event and without the machine's own clock.
 */

import { eventAvailability } from "@/lib/availability";
import { eventBookingDeadline } from "./booking-deadline";
import { LIVE_CARD_LEAD_MS, localDayKey } from "./live-event-card-window";

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Under ten minutes the seconds are the story — a drop is about to happen, and
 * a label that only moves once a minute looks stuck.
 */
const SECONDS_ZONE_MS = 10 * MINUTE;

/** Inside this, "closes in 4h" is news rather than trivia. */
const CLOSING_ATTENTION_MS = 6 * HOUR;

/** Inside this, an imminent drop earns the same treatment. */
const OPENING_ATTENTION_MS = HOUR;

/**
 * How early gates get their own line. The same twelve hours the day-of ticket
 * card uses, so the two surfaces start talking about the door at the same
 * moment.
 */
const GATES_LEAD_MS = LIVE_CARD_LEAD_MS;

export type BookingWindowPhase =
  /** Window announced, not yet open. */
  | "pre_open"
  /** Bookable right now, counting down to the deadline. */
  | "open"
  /** Window shut, event still ahead. */
  | "closed_before_start"
  /** Nothing left to say about booking; the door is the next thing to happen. */
  | "gates_soon"
  /** Underway. */
  | "live"
  /** This occurrence is done, but the event runs again. */
  | "next_date"
  /** Done, with nothing after it. */
  | "ended";

/**
 * Treatment, not identity. `attention` is amber, never red — a deadline is not
 * a failure — and `muted` is for everything already settled.
 */
export type BookingWindowTone = "neutral" | "attention" | "live" | "muted";

export type BookingWindowIcon =
  | "hourglass"
  | "calendar-clock"
  | "door-open"
  | "lock"
  | "live"
  | "check";

export interface BookingWindowState {
  phase: BookingWindowPhase;
  tone: BookingWindowTone;
  icon: BookingWindowIcon;
  /** The live fact. */
  headline: string;
  /** The absolute anchor for the headline's claim, or null when it has none. */
  subline: string | null;
  /** What the headline counts toward; null when it counts toward nothing. */
  target: Date | null;
  /**
   * Milliseconds until the copy could change, for the caller's ticker. Null
   * means it never will, so no timer should exist at all.
   */
  tickMs: number | null;
  /** The occurrence this copy is about — not always the one passed in. */
  occurrenceId: number;
}

export interface BookingWindowOccurrence {
  id: number;
  start: Date;
  end: Date;
  bookingOpensAt: Date | null;
  bookingClosesAt: Date | null;
  gatesOpenAt?: Date | null;
  /**
   * Every active pass is gone. A deadline countdown on tickets nobody can buy
   * is a lie, so this suppresses the urgency and lets the footer say sold out.
   */
  soldOut?: boolean;
}

/** The configured deadline, falling back to start and capped at event end. */
export function effectiveBookingCloseAt(
  occurrence: Pick<
    BookingWindowOccurrence,
    "start" | "end" | "bookingClosesAt"
  >,
): Date {
  return eventBookingDeadline({
    startTime: occurrence.start,
    endTime: occurrence.end,
    bookingClosesAt: occurrence.bookingClosesAt,
  });
}

/** Whether the deadline is the event itself rather than a configured close. */
export function closesAtEventStart(
  occurrence: Pick<
    BookingWindowOccurrence,
    "start" | "end" | "bookingClosesAt"
  >,
): boolean {
  return (
    effectiveBookingCloseAt(occurrence).getTime() === occurrence.start.getTime()
  );
}

/**
 * Sold out across every pass that is still on sale. Unlimited passes report a
 * null remainder, so a single one of those keeps the occurrence sellable.
 */
export function isOccurrenceSoldOut(occurrence: {
  remaining?: number | null;
  passes?: Array<{
    isActive: boolean;
    remaining: number | null;
    attendeesPerPass?: number;
  }>;
}): boolean {
  return eventAvailability(occurrence).state === "sold_out";
}

type CountdownPrecision = "absolute" | "minutes" | "seconds";

function precisionFor(distanceMs: number): CountdownPrecision {
  if (distanceMs > DAY) return "absolute";
  if (distanceMs > SECONDS_ZONE_MS) return "minutes";
  return "seconds";
}

/**
 * When the caller should look again.
 *
 * Beyond a day nothing on screen moves until the 24h boundary opens the
 * countdown zone — but a page left open overnight has had its timers suspended
 * by the OS, so cap the wait at half an hour and let it re-sync.
 */
function tickMsForTarget(now: Date, target: Date): number {
  const distance = target.getTime() - now.getTime();
  if (distance <= SECONDS_ZONE_MS) return SECOND;
  if (distance <= DAY) return MINUTE;
  return Math.min(distance - DAY, 30 * MINUTE);
}

/** `6h 12m`, `42m`, `04:32` — never a bare "soon". */
export function formatCountdown(
  distanceMs: number,
  precision: CountdownPrecision,
): string {
  const remaining = Math.max(0, distanceMs);

  if (precision === "seconds") {
    const totalSeconds = Math.floor(remaining / SECOND);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  const hours = Math.floor(remaining / HOUR);
  const minutes = Math.floor((remaining % HOUR) / MINUTE);
  if (hours >= 1) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${Math.max(1, minutes)}m`;
}

/**
 * "16 days ago", "in 18 hours", "tomorrow".
 *
 * One coarse unit, because this is orientation rather than a countdown: it is
 * only ever shown beside the absolute time it describes, and "in 18h 25m" next
 * to "9:59 AM" makes a reader do arithmetic to check the two agree.
 *
 * Day-scale spans are counted in **calendar** days, not in 24-hour windows.
 * "Tomorrow" is a property of the calendar: 30 hours from a Friday evening is
 * Sunday, and rounding the elapsed time called that "tomorrow" while the date
 * beside it said Sun, Sep 6. Hours win below a day, so a deadline 15 hours out
 * reads "in 15 hours" rather than a vaguer "tomorrow".
 */
export function formatRelativeSpan(
  target: Date,
  now: Date,
  timeZone?: string,
): string {
  const deltaMs = target.getTime() - now.getTime();
  const magnitude = Math.abs(deltaMs);

  if (magnitude < MINUTE) {
    return deltaMs >= 0 ? "in under a minute" : "just now";
  }
  if (magnitude < HOUR) {
    return RELATIVE_FORMATTER.format(Math.round(deltaMs / MINUTE), "minute");
  }
  if (magnitude < DAY) {
    return RELATIVE_FORMATTER.format(Math.round(deltaMs / HOUR), "hour");
  }

  const days = calendarDayOffset(target, now, resolveTimeZone(timeZone));
  if (Math.abs(days) < 45) {
    return RELATIVE_FORMATTER.format(days, "day");
  }
  return RELATIVE_FORMATTER.format(Math.round(days / 30), "month");
}

/**
 * "16 hours", "1 hour 30 minutes", "2 days 4 hours" — spelled out, because it
 * appears once, in a heading, where "16h" reads like a truncation.
 */
export function formatDurationWords(durationMs: number): string {
  const total = Math.max(0, durationMs);
  const plural = (value: number, unit: string) =>
    `${value} ${unit}${value === 1 ? "" : "s"}`;

  if (total < HOUR) {
    return plural(Math.max(1, Math.round(total / MINUTE)), "minute");
  }
  if (total < DAY) {
    const hours = Math.floor(total / HOUR);
    const minutes = Math.round((total % HOUR) / MINUTE);
    return minutes > 0
      ? `${plural(hours, "hour")} ${plural(minutes, "minute")}`
      : plural(hours, "hour");
  }
  const days = Math.floor(total / DAY);
  const hours = Math.round((total % DAY) / HOUR);
  return hours > 0
    ? `${plural(days, "day")} ${plural(hours, "hour")}`
    : plural(days, "day");
}

const RELATIVE_FORMATTER = new Intl.RelativeTimeFormat("en-US", {
  numeric: "auto",
});

const TIME_FORMATTERS = new Map<string, Intl.DateTimeFormat>();
const DATE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();
const DATE_YEAR_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function resolveTimeZone(timeZone?: string): string {
  return timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";
}

function cachedFormatter(
  cache: Map<string, Intl.DateTimeFormat>,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const existing = cache.get(timeZone);
  if (existing) return existing;
  const formatter = new Intl.DateTimeFormat("en-US", { ...options, timeZone });
  cache.set(timeZone, formatter);
  return formatter;
}

/** `7:00 PM` */
export function formatClockTime(date: Date, timeZone?: string): string {
  return cachedFormatter(TIME_FORMATTERS, resolveTimeZone(timeZone), {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
}

/** `Sat, 20 Sep` — with the year only when it is not this one. */
export function formatCalendarDate(
  date: Date,
  now: Date,
  timeZone?: string,
): string {
  const zone = resolveTimeZone(timeZone);
  const sameYear =
    localDayKey(date, zone).slice(0, 4) === localDayKey(now, zone).slice(0, 4);
  return sameYear
    ? cachedFormatter(DATE_FORMATTERS, zone, {
        weekday: "short",
        day: "numeric",
        month: "short",
      }).format(date)
    : cachedFormatter(DATE_YEAR_FORMATTERS, zone, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(date);
}

/**
 * `today at 7:00 PM`, `tomorrow at 7:00 PM`, `Sat, Sep 20 · 7:00 PM`
 *
 * The relative forms stay lowercase because every line in this module puts a
 * moment mid-sentence — "Bookings open today at 7:00 PM", not "Bookings open
 * Today at 7:00 PM".
 */
export function formatMoment(date: Date, now: Date, timeZone?: string): string {
  const zone = resolveTimeZone(timeZone);
  const dayOffset = calendarDayOffset(date, now, zone);
  if (dayOffset === 0) return `today at ${formatClockTime(date, zone)}`;
  if (dayOffset === 1) return `tomorrow at ${formatClockTime(date, zone)}`;
  if (dayOffset === -1) return `yesterday at ${formatClockTime(date, zone)}`;
  return `${formatCalendarDate(date, now, zone)} · ${formatClockTime(date, zone)}`;
}

/**
 * Whole local days between two instants, by calendar key rather than by
 * elapsed milliseconds — 11pm to 1am is one day apart, not zero.
 */
function calendarDayOffset(date: Date, now: Date, timeZone: string): number {
  const target = Date.parse(`${localDayKey(date, timeZone)}T00:00:00Z`);
  const today = Date.parse(`${localDayKey(now, timeZone)}T00:00:00Z`);
  return Math.round((target - today) / DAY);
}

function isSameZonedDay(first: Date, second: Date, timeZone: string): boolean {
  return localDayKey(first, timeZone) === localDayKey(second, timeZone);
}

/** The headline's relative half, or its absolute half when it is far off. */
function describeTarget(
  now: Date,
  target: Date,
  timeZone: string,
): { precision: CountdownPrecision; countdown: string; moment: string } {
  const distance = target.getTime() - now.getTime();
  const precision = precisionFor(distance);
  return {
    precision,
    countdown: formatCountdown(distance, precision),
    moment: formatMoment(target, now, timeZone),
  };
}

export function resolveBookingWindowState(input: {
  now: Date;
  occurrence: BookingWindowOccurrence | null | undefined;
  /** Ascending, and may include `occurrence` itself. */
  upcoming?: BookingWindowOccurrence[];
  timeZone?: string;
}): BookingWindowState | null {
  const { now, occurrence } = input;
  if (!occurrence) return null;

  const zone = resolveTimeZone(input.timeZone);

  // --- Over. Talk about the next date instead of the corpse. ------------
  if (now.getTime() >= occurrence.end.getTime()) {
    const next = (input.upcoming ?? [])
      .filter(
        (candidate) =>
          candidate.id !== occurrence.id &&
          candidate.start.getTime() > now.getTime(),
      )
      .sort(
        (first, second) => first.start.getTime() - second.start.getTime(),
      )[0];

    if (next) {
      const opensAt = next.bookingOpensAt;
      const waitingToOpen = Boolean(
        opensAt && opensAt.getTime() > now.getTime(),
      );
      const target = waitingToOpen && opensAt ? opensAt : next.start;
      const described = describeTarget(now, target, zone);

      return {
        phase: "next_date",
        tone: "neutral",
        icon: "calendar-clock",
        headline: `Next date ${formatMoment(next.start, now, zone)}`,
        subline: waitingToOpen
          ? described.precision === "absolute"
            ? `Bookings open ${described.moment}`
            : `Bookings open in ${described.countdown}`
          : "Booking open now",
        target,
        tickMs: tickMsForTarget(now, target),
        occurrenceId: next.id,
      };
    }

    return {
      phase: "ended",
      tone: "muted",
      icon: "check",
      headline: `Ended ${formatCalendarDate(occurrence.end, now, zone)}`,
      subline: null,
      target: null,
      tickMs: null,
      occurrenceId: occurrence.id,
    };
  }

  // --- Underway. -------------------------------------------------------
  if (now.getTime() >= occurrence.start.getTime()) {
    const deadline = effectiveBookingCloseAt(occurrence);
    const bookingsOpen =
      !occurrence.soldOut &&
      (!occurrence.bookingOpensAt || occurrence.bookingOpensAt <= now) &&
      now < deadline;
    const target = now < deadline ? deadline : occurrence.end;
    return {
      phase: "live",
      tone: "live",
      icon: "live",
      headline: "Happening now",
      subline: bookingsOpen
        ? `Booking closes ${formatMoment(deadline, now, zone)}`
        : `Ends at ${formatClockTime(occurrence.end, zone)}`,
      // Refresh eligibility when the live event's booking window closes.
      target,
      tickMs: tickMsForTarget(now, target),
      occurrenceId: occurrence.id,
    };
  }

  // --- Announced, not yet open. ----------------------------------------
  const opensAt = occurrence.bookingOpensAt;
  if (opensAt && now.getTime() < opensAt.getTime()) {
    const described = describeTarget(now, opensAt, zone);
    const distance = opensAt.getTime() - now.getTime();

    return {
      phase: "pre_open",
      tone: distance <= OPENING_ATTENTION_MS ? "attention" : "neutral",
      icon: "hourglass",
      headline:
        described.precision === "absolute"
          ? `Bookings open ${described.moment}`
          : `Bookings open in ${described.countdown}`,
      subline:
        described.precision === "absolute"
          ? `Event starts ${formatMoment(occurrence.start, now, zone)}`
          : `Opens ${described.moment}`,
      target: opensAt,
      tickMs: tickMsForTarget(now, opensAt),
      occurrenceId: occurrence.id,
    };
  }

  // --- Window shut, event still ahead. ---------------------------------
  const closesAt = occurrence.bookingClosesAt;
  if (closesAt && now.getTime() >= closesAt.getTime()) {
    const gatesOpenAt = occurrence.gatesOpenAt ?? null;
    const gatesAhead =
      gatesOpenAt &&
      gatesOpenAt.getTime() > now.getTime() &&
      gatesOpenAt.getTime() - now.getTime() <= GATES_LEAD_MS;

    if (gatesAhead && gatesOpenAt) {
      const described = describeTarget(now, gatesOpenAt, zone);
      return {
        phase: "gates_soon",
        tone: "attention",
        icon: "door-open",
        headline:
          described.precision === "absolute"
            ? `Gates open ${described.moment}`
            : `Gates open in ${described.countdown}`,
        subline: `Starts at ${formatClockTime(occurrence.start, zone)}`,
        target: gatesOpenAt,
        tickMs: tickMsForTarget(now, gatesOpenAt),
        occurrenceId: occurrence.id,
      };
    }

    const sameDay = isSameZonedDay(closesAt, occurrence.start, zone);
    return {
      phase: "closed_before_start",
      tone: "muted",
      icon: "lock",
      headline: "Bookings closed",
      subline: sameDay
        ? `Closed ${formatClockTime(closesAt, zone)} · Starts ${formatClockTime(occurrence.start, zone)}`
        : `Closed ${formatMoment(closesAt, now, zone)}`,
      target: occurrence.start,
      tickMs: tickMsForTarget(now, occurrence.start),
      occurrenceId: occurrence.id,
    };
  }

  // --- Open. -----------------------------------------------------------
  const deadline = effectiveBookingCloseAt(occurrence);
  const atStart = closesAtEventStart(occurrence);

  if (occurrence.soldOut) {
    // No deadline pressure on tickets nobody can buy. The footer owns the
    // sold-out message; this row goes back to plain schedule facts.
    const gatesOpenAt = occurrence.gatesOpenAt ?? null;
    return {
      phase: "open",
      tone: "muted",
      icon: "calendar-clock",
      headline: `Starts ${formatMoment(occurrence.start, now, zone)}`,
      subline: gatesOpenAt
        ? `Gates open ${formatClockTime(gatesOpenAt, zone)}`
        : null,
      target: occurrence.start,
      tickMs: tickMsForTarget(now, occurrence.start),
      occurrenceId: occurrence.id,
    };
  }

  const described = describeTarget(now, deadline, zone);
  const distance = deadline.getTime() - now.getTime();

  const headline =
    described.precision === "absolute"
      ? atStart
        ? "Booking closes when the event starts"
        : `Booking closes ${described.moment}`
      : `Booking closes in ${described.countdown}`;

  const subline =
    described.precision === "absolute"
      ? atStart
        ? `Starts ${formatMoment(occurrence.start, now, zone)}`
        : `Event starts ${formatMoment(occurrence.start, now, zone)}`
      : atStart
        ? `Closes when the event starts, ${formatClockTime(occurrence.start, zone)}`
        : `Closes ${formatMoment(deadline, now, zone)}`;

  return {
    phase: "open",
    tone: distance <= CLOSING_ATTENTION_MS ? "attention" : "neutral",
    icon: "calendar-clock",
    headline,
    subline,
    target: deadline,
    tickMs: tickMsForTarget(now, deadline),
    occurrenceId: occurrence.id,
  };
}
